/**
 * «¿ESTE PAGO ES DE ALGUNA DE ESTAS CITAS CON RESERVA?» (12/10 y 15/10/2026)
 * ------------------------------------------------------------------------
 * Un cobro deja pagada la reserva de una cita cuando su concepto es de los
 * que la pagan: lo dice la configuración de su tipo de cita (Gestión de
 * agendas → Conceptos de cita: «Cómo se paga la reserva» y «Cobros que la
 * pagan»; backend/services/reserva_cita.py). Lo mismo en las tres pantallas
 * que apuntan cobros: Cobros, la ficha del paciente (cobro rápido) y el
 * Datáfono.
 *   · El servidor da, una vez por paciente, sus citas con la reserva sin
 *     pagar que se pagan con un cobro, cada una con sus textos; aquí se
 *     quedan las que paga el concepto que se va escribiendo (la misma regla
 *     que el servidor: sin mayúsculas, tildes ni espacios sobrantes).
 *   · con UNA o con VARIAS se pregunta SIEMPRE (revisión del 15/10/2026: un
 *     pago de otra cosa pagaba sin querer la única reserva pendiente): una
 *     opción por cita y «No, es de otra cosa» (`NINGUNA`), sin nada elegido
 *     de antemano, y es obligatorio: la pantalla no deja guardar sin
 *     contestar (`falta`). El servidor, si le llega sin contestar (el
 *     conector, una API vieja), enlaza la única o la más próxima y el rastro
 *     dice que llegó sin elegir;
 *   · sin ninguna (o una devolución), nada.
 * Al cambiar de paciente la lista se vacía en el momento: nunca se ve la del
 * paciente anterior mientras llega la nueva.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api, formatDate } from '../../lib/api';
import { isDevolucionConcept } from '../../lib/cobroPresets';
import { cobroPagaLaReserva, nombreReserva, normalizarTextoCobro } from '../../lib/citasApi';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/** La respuesta «No, es de otra cosa» (services/reserva_cita.NINGUNA). */
export const NINGUNA = 'ninguna';

/** «20/01/2099 · 10:00 · Reserva consulta · Consulta Dra. X». */
export function textoCitaReserva(c) {
  const servicio = c.servicio || (c.concept_name ? nombreReserva(c.concept_name) : '');
  return [formatDate(c.date), c.start, servicio, c.agenda_name].filter(Boolean).join(' · ');
}

/**
 * Estado del campo para un paciente y un concepto: las citas con la reserva
 * sin pagar que paga ese concepto, la elegida y lo que va en el cobro
 * (`reservaCitaId`, '' = nada: con una, o sin ninguna, el servidor decide
 * solo).
 */
export function useReservasPendientes(patientId, concept) {
  const conConcepto = !!normalizarTextoCobro(concept) && !isDevolucionConcept(concept);
  // Las de UN paciente: si no son las del que está elegido, no cuentan.
  const [cargadas, setCargadas] = useState({ pid: '', citas: [] });
  const [citaId, setCitaId] = useState('');
  useEffect(() => {
    setCargadas({ pid: '', citas: [] });
    if (!patientId || !conConcepto) return undefined;
    let vigente = true;
    api.get('/api/cobros/reservas-pendientes', { params: { patient_id: patientId } })
      .then((r) => {
        if (vigente) setCargadas({ pid: patientId, citas: Array.isArray(r.data) ? r.data : [] });
      })
      // Sin la lista, el servidor enlaza igual (la única, o la más próxima).
      .catch(() => { if (vigente) setCargadas({ pid: '', citas: [] }); });
    return () => { vigente = false; };
  }, [patientId, conConcepto]);
  const lista = useMemo(() => {
    if (!conConcepto || cargadas.pid !== patientId) return [];
    return cargadas.citas.filter((c) => cobroPagaLaReserva(concept, c.reserva_cobros));
  }, [cargadas, patientId, concept, conConcepto]);
  // La respuesta deja de valer si cambian el paciente o las citas que paga.
  const firma = lista.map((c) => c.id).join('|');
  useEffect(() => { setCitaId(''); }, [patientId, firma]);
  const pregunta = lista.length > 0;
  const elegida = pregunta && (citaId === NINGUNA || lista.some((c) => c.id === citaId)) ? citaId : '';
  return {
    pagaReservas: pregunta,
    pregunta,
    lista,
    citaId: elegida,
    setCitaId,
    falta: pregunta && !elegida,
    // Lo que va en el cobro: la cita, o «ninguna».
    reservaCitaId: elegida,
  };
}

/**
 * El campo. `prefijo` da los data-testid: `<prefijo>-cita` (el selector, con
 * `-select` en su botón) y `<prefijo>-una` (el aviso con una sola).
 */
export function ReservaDeQueCitaField({ estado, prefijo = 'cobro-reserva', className = '' }) {
  if (!estado.pregunta) return null;
  const n = estado.lista.length;
  return (
    <div className={className} data-testid={`${prefijo}-cita`}>
      <Label>¿Este pago es de alguna de estas citas con reserva? <span className="text-red-600">*</span></Label>
      <Select value={estado.citaId} onValueChange={estado.setCitaId}>
        <SelectTrigger className="mt-1.5" data-testid={`${prefijo}-cita-select`}>
          <SelectValue placeholder="— Contesta: ¿de qué cita es? —" />
        </SelectTrigger>
        <SelectContent>
          {estado.lista.map((c) => (
            <SelectItem key={c.id} value={c.id}>{textoCitaReserva(c)}</SelectItem>
          ))}
          <SelectItem value={NINGUNA}>No, es de otra cosa</SelectItem>
        </SelectContent>
      </Select>
      <div className="text-xs text-muted-foreground mt-1">
        {n === 1
          ? 'Este paciente tiene una cita con la reserva sin pagar que se paga con este concepto.'
          : `Este paciente tiene ${n} citas con la reserva sin pagar que se pagan con este concepto.`}
        {' '}La que elijas queda pagada; si el pago es de otra cosa, ninguna.
      </div>
    </div>
  );
}
